import type { ProductCardDTO } from "../../api/types.js";
import {
  customerStockLabel,
  shouldRenderPurchaseButton,
  unavailablePurchaseHint,
} from "../../lib/purchasable.js";
import { formatCustomerPayablePrice } from "../../lib/format.js";
import { messages } from "../../i18n/messages.js";

interface ProductCardProps {
  product: ProductCardDTO;
  layout?: "list" | "grid";
  onSelect: (product: ProductCardDTO) => void;
  onAddToCart?: (product: ProductCardDTO) => void;
  addDisabled?: boolean;
}

function PromoBadge({ label }: { label: string }) {
  return (
    <span className="absolute left-2 top-2 rounded-full bg-orange-600 px-2 py-0.5 text-[10px] font-semibold text-white">
      {label}
    </span>
  );
}

function MetaLine({ product }: { product: ProductCardDTO }) {
  const parts = [product.brand, product.country, product.weight_or_volume].filter(Boolean);
  if (parts.length === 0) {
    return null;
  }
  return <p className="line-clamp-1 text-[11px] opacity-60">{parts.join(" · ")}</p>;
}

export function ProductCard({
  product,
  layout = "list",
  onSelect,
  onAddToCart,
  addDisabled = false,
}: ProductCardProps) {
  const stockLabel = customerStockLabel(product);
  const canAdd = shouldRenderPurchaseButton(product, Boolean(onAddToCart));
  const unavailableHint = unavailablePurchaseHint(product);

  if (layout === "grid") {
    return (
      <article className="flex flex-col overflow-hidden rounded-[16px] border border-black/5 bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)]">
        <button
          type="button"
          onClick={() => onSelect(product)}
          className="flex flex-1 flex-col text-left active:opacity-80"
        >
          <div className="relative aspect-square w-full overflow-hidden bg-black/5">
            {product.is_promotional && product.promo_label ? (
              <PromoBadge label={product.promo_label} />
            ) : null}
            {product.image_url ? (
              <img
                src={product.image_url}
                alt=""
                className="h-full w-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xs opacity-40">
                {messages.common.noImage}
              </div>
            )}
          </div>
          <div className="flex flex-1 flex-col gap-1 p-2.5">
            <p className="line-clamp-2 text-sm font-medium leading-snug">{product.title}</p>
            <MetaLine product={product} />
            <p className="text-sm font-semibold">{formatCustomerPayablePrice(product)}</p>
            <p className="text-[11px] opacity-60">{stockLabel}</p>
            {!product.price_resolved ? (
              <p className="text-[10px] text-amber-700">{messages.catalog.priceUnresolved}</p>
            ) : null}
          </div>
        </button>
        {canAdd ? (
          <button
            type="button"
            disabled={addDisabled}
            aria-label={messages.catalog.addToCart}
            onClick={() => onAddToCart!(product)}
            className="mx-2.5 mb-2.5 min-h-11 rounded-[14px] bg-[var(--tg-theme-button-color,#3390ec)] text-sm font-medium text-[var(--tg-theme-button-text-color,#ffffff)] disabled:opacity-50"
          >
            +
          </button>
        ) : unavailableHint ? (
          <p className="mx-2.5 mb-2.5 text-center text-[11px] font-medium opacity-60">{unavailableHint}</p>
        ) : null}
      </article>
    );
  }

  return (
    <article className="flex w-full gap-3 rounded-[16px] border border-black/5 bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] p-3">
      <button
        type="button"
        onClick={() => onSelect(product)}
        className="flex min-w-0 flex-1 gap-3 text-left transition active:opacity-80"
      >
        <div className="relative h-[72px] w-[72px] shrink-0 overflow-hidden rounded-[14px] bg-black/5">
          {product.is_promotional && product.promo_label ? (
            <PromoBadge label={product.promo_label} />
          ) : null}
          {product.image_url ? (
            <img
              src={product.image_url}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs opacity-40">
              {messages.common.noImage}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-sm font-medium leading-snug">{product.title}</p>
          <MetaLine product={product} />
          <p className="mt-1 text-sm font-semibold">{formatCustomerPayablePrice(product)}</p>
          <p className="mt-1 text-[11px] opacity-60">{stockLabel}</p>
          {!product.price_resolved ? (
            <p className="mt-1 text-[10px] text-amber-700">{messages.catalog.priceUnresolved}</p>
          ) : null}
        </div>
      </button>
      {canAdd ? (
        <button
          type="button"
          disabled={addDisabled}
          aria-label={messages.catalog.addToCart}
          onClick={() => onAddToCart!(product)}
          className="flex h-11 w-11 shrink-0 items-center justify-center self-center rounded-full bg-[var(--tg-theme-button-color,#3390ec)] text-lg text-[var(--tg-theme-button-text-color,#ffffff)] disabled:opacity-50"
        >
          +
        </button>
      ) : unavailableHint ? (
        <p className="flex w-20 shrink-0 items-center justify-center self-center text-center text-[10px] font-medium opacity-60">
          {unavailableHint}
        </p>
      ) : null}
    </article>
  );
}
